import { useSelector } from 'react-redux';
import { VIDEO_REMOVE_BG_USE_CREDIT } from '../redux/slices/FJSliceVideoBgRemover';
import FJLocalStore from '../store/FJLocalStore';
import FJAppConstant from '../constants/FJAppConstant';
import { IMAGE_TO_IMAGE_USE_CREDIT } from '../redux/slices/FJSliceImageToImage';
import { IMAGE_ENHANCER_USE_CREDIT } from '../redux/slices/FJSliceImageEnhancer';
import { IMAGE_EXTENDER_USE_CREDIT } from '../redux/slices/FJSliceImageExtender';
import { useMemo } from 'react';
import FJTextToImageDataConfig from '../config/dataConfig/FJTextToImageDataConfig';
import { AI_AUDIO_USE_CREDIT } from '../redux/slices/FJSliceAIAudioGenerator';
import { AI_VIDEO_DUB_USE_CREDIT } from '../redux/slices/FJSliceAiVideoDub';
import FJTextBasedEditingConfig from '../config/appConfig/FJTextBasedEditingConfig';

const useUsageDetailContainer = () => {
    const totalTime = useSelector(state => state.videoBgRemover.totalTime);
    const appType = useSelector(state => state.common.appType);
    const imageToImageGenerateQuantity = useSelector(state => state.imageToImage.generateQuantity);
    const imageEnhancerGenerateQuantity = useSelector(state => state.imageEnhancer.uploadImageList.length);
    const imageExtenderGenerateQuantity = useSelector(state => state.imageExtender.generateNumber);
    const textToImageGenerateQuantity = useSelector(state => state.textToImage.generateNumber);
    const textToImageCurModel = useSelector(state => state.textToImage.curModel);
    const videoGeneratorCurrentQuality = useSelector(state => state.AIVideoGenerator.currentQuality);
    const videoGeneratorCurrentDuration = useSelector(state => state.AIVideoGenerator.currentDuration);
    const videoGeneratorTotalCredit = useSelector(state => state.AIVideoGenerator.totalCredit);
    const videoGeneratorCreditPrice = useSelector(state => state.AIVideoGenerator.creditPrice);
    const aiVideoDubTotalCredit = useSelector(state => state.aiVideoDub.useCredit);
    const aiVideoDubTrimInfo = useSelector(state => state.aiVideoDub.trimInfo);
    const textBasedEditingDuration = useSelector(state => state.textBasedEditing.originalResourceData.duration);

    const textToImageUseCredit = useMemo(() => {
        const modelUseCredit = FJTextToImageDataConfig.MODELS.find(
            item => item.value === textToImageCurModel,
        )?.useCredit;
        const useCredit = modelUseCredit * textToImageGenerateQuantity;

        return useCredit;
    }, [textToImageCurModel, textToImageGenerateQuantity]);

    const getDataList = () => {
        switch (appType) {
            case FJAppConstant.VIDEO_REMOVE_BG: {
                const label1 = 'remove-background';
                const value1 = `${VIDEO_REMOVE_BG_USE_CREDIT} ${FJLocalStore._('credits')}/s`;
                const label2 = 'duration';
                const value2 = `${Math.floor(totalTime)} s`;
                const tutalCredit = (Math.floor(totalTime) * VIDEO_REMOVE_BG_USE_CREDIT).toFixed(1);

                return {
                    list: [{ label1: label1, value1: value1, label2: label2, value2: value2 }],
                    tutalCredit,
                };
            }
            case FJAppConstant.IMAGE_TO_IMAGE: {
                const label1 = 'image-to-image';
                const value1 = `${FJLocalStore._(imageToImageGenerateQuantity === 1 ? 'use-credit' : 'use-credits').replace('xxx', imageToImageGenerateQuantity)}`;
                const label2 = 'count';
                const value2 = `${imageToImageGenerateQuantity}`;
                const tutalCredit = (imageToImageGenerateQuantity * IMAGE_TO_IMAGE_USE_CREDIT).toFixed(1);

                return {
                    list: [{ label1: label1, value1: value1, label2: label2, value2: value2 }],
                    tutalCredit: tutalCredit,
                };
            }
            case FJAppConstant.IMAGE_ENHANCER: {
                const label1 = 'upscale';
                const value1 = `${FJLocalStore._(IMAGE_ENHANCER_USE_CREDIT === 1 ? 'use-credit' : 'use-credits').replace('xxx', IMAGE_ENHANCER_USE_CREDIT)}`;
                const label2 = 'count';
                const value2 = `${Math.max(imageEnhancerGenerateQuantity, 0)}`;
                const tutalCredit = (Math.max(imageEnhancerGenerateQuantity, 0) * IMAGE_ENHANCER_USE_CREDIT).toFixed(1);

                return {
                    list: [{ label1: label1, value1: value1, label2: label2, value2: value2 }],
                    tutalCredit: tutalCredit,
                };
            }
            case FJAppConstant.IMAGE_EXTENDER: {
                const label1 = 'extend';
                const value1 = `${FJLocalStore._(IMAGE_EXTENDER_USE_CREDIT === 1 ? 'use-credit' : 'use-credits').replace('xxx', IMAGE_EXTENDER_USE_CREDIT)}`;
                const label2 = 'count';
                const value2 = `${Math.max(imageExtenderGenerateQuantity, 0)}`;
                const tutalCredit = (Math.max(imageExtenderGenerateQuantity, 0) * IMAGE_EXTENDER_USE_CREDIT).toFixed(1);

                return {
                    list: [{ label1: label1, value1: value1, label2: label2, value2: value2 }],
                    tutalCredit: tutalCredit,
                };
            }
            case FJAppConstant.TEXT_TO_IMAGE: {
                let list = [];

                FJTextToImageDataConfig.MODELS.forEach(item => {
                    if (textToImageCurModel === item.value) {
                        list.push({
                            label1: item.name,
                            value1: `${FJLocalStore._(item.useCredit === 1 ? 'use-credit' : 'use-credits').replace('xxx', item.useCredit)}`,
                            label2: 'count',
                            value2: `${textToImageGenerateQuantity}`,
                        });
                    }
                });
                return {
                    list: list,
                    tutalCredit: textToImageUseCredit,
                };
            }
            case FJAppConstant.AI_VIDEO_GENERATOR: {
                const label1 = FJLocalStore._(videoGeneratorCurrentQuality === 1 ? 'standard' : 'professional');
                const value1 = `${videoGeneratorCreditPrice} ${FJLocalStore._('credits-s')}`;
                const label2 = 'duration';
                const value2 = `${videoGeneratorCurrentDuration}s`;

                return {
                    list: [{ label1: label1, value1: value1, label2: label2, value2: value2 }],
                    tutalCredit: videoGeneratorTotalCredit,
                };
            }

            case FJAppConstant.AI_AUDIO_GENERATOR: {
                const label1 = FJLocalStore._('ai-music-generator');
                const value1 = `${AI_AUDIO_USE_CREDIT} ${FJLocalStore._('credits-time')}`;
                const label2 = FJLocalStore._('current-consumption');
                const value2 = FJLocalStore._('1-time');

                return {
                    list: [{ label1: label1, value1: value1, label2: label2, value2: value2 }],
                    tutalCredit: AI_AUDIO_USE_CREDIT,
                };
            }

            case FJAppConstant.AI_VIDEO_DUB: {
                const label1 = FJLocalStore._('ai-sfx');
                const value1 = `${AI_VIDEO_DUB_USE_CREDIT} ${FJLocalStore._('credits-s')}`;
                const label2 = 'duration';
                const value2 = `${Math.floor(aiVideoDubTrimInfo.end - aiVideoDubTrimInfo.start)}s`;

                return {
                    list: [{ label1: label1, value1: value1, label2: label2, value2: value2 }],
                    tutalCredit: aiVideoDubTotalCredit,
                };
            }

            case FJAppConstant.AI_TEXT_BASED_EDITING: {
                const duration = Math.ceil(textBasedEditingDuration / 60);

                const label1 = FJLocalStore._('transcribe');
                const value1 = `${FJTextBasedEditingConfig.unitPrice} ${FJLocalStore._('credits-minute')}`;
                const label2 = FJLocalStore._('duration');
                const value2 = `${duration} ${FJLocalStore._('minutes').toLowerCase()}`;

                return {
                    list: [{ label1: label1, value1: value1, label2: label2, value2: value2 }],
                    tutalCredit: (duration * FJTextBasedEditingConfig.unitPrice).toFixed(1),
                };
            }
            default:
                return { list: [], tutalCredit: 0 };
        }
    };

    const { list, tutalCredit } = getDataList();

    return {
        list,
        tutalCredit,
    };
};

export default useUsageDetailContainer;
